import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { theme } from 'styles/theme/hav-theme'

type ContainerProps = {
  width?: number
}

type DropdownProps = {
  height: number
  open: boolean
}

type FieldProps = {
  error: boolean
  disabled: boolean
  height: number
}

export const MultiselectContainer = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;

  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `}
`

export const LabelWrapper = styled.div<Pick<FieldProps, 'disabled'>>`
  ${({ disabled }) =>
    disabled &&
    css`
      #multiselect-label {
        color: ${theme.colors.neutral.medium};
      }
    `}
`

export const FieldWrapper = styled.div<FieldProps>`
  display: flex;
  align-items: center;
  position: relative;
  height: ${({ height }) => `${height}px`};
  background: ${theme.colors.neutral.white};
  border-radius: ${theme.shapes.borderRadius.sm};
  border: ${theme.shapes.borderWidth.thin} ${theme.colors.neutral.mediumhover};

  :focus-within {
    border: ${theme.shapes.borderWidth.thin} ${theme.colors.primary.main};
  }

  ${({ error }) =>
    error &&
    css`
      border-color: ${theme.colors.alert.danger.main};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      border-color: ${theme.colors.neutral.clear};
      background-color: ${theme.colors.neutral[100]};

      input {
        ::placeholder {
          color: ${theme.colors.neutral.mediumhover};
        }
      }
    `}
`

export const FieldContainer = styled.div<ContainerProps>`
  display: flex;
  max-width: ${({ width }) => (width ? `${width}px` : '100%')};
  align-items: center;
  gap: ${theme.spacing.xs};
  margin: 0 32px 0 ${theme.spacing.sm};
  overflow-x: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`

export const DropdownButton = styled.button<DropdownProps>`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  width: fit-content;
  padding: ${theme.spacing.sm};
  background: ${theme.colors.neutral.white};
  border-radius: ${theme.shapes.borderRadius.sm};
  height: ${({ height }) => `${height - 2}px`};

  svg {
    path {
      fill: ${theme.colors.primary.main};
    }
  }

  :disabled {
    background-color: ${theme.colors.neutral[100]};

    svg {
      path {
        fill: ${theme.colors.neutral.mediumhover};
      }
    }
  }

  ${({ open }) =>
    !open &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`

export const DropdownContainer = styled.div<Pick<DropdownProps, 'height'>>`
  position: absolute;
  min-width: 100%;
  top: ${({ height }) => `${height}px`};
  display: flex;
  padding: ${theme.spacing.xm};
  background: ${theme.colors.neutral.white};
  box-shadow: ${theme.effects.boxShadow['rule-01']};
  border-radius: ${theme.shapes.borderRadius.sm};
  z-index: 100;
`

export const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 205px;
  gap: ${theme.spacing.xs};

  overflow: hidden;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: ${theme.spacing.sm};
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.neutral.clear};
    border-radius: ${theme.shapes.borderRadius.md};
  }

  ::-webkit-scrollbar-thumb {
    border: none;
    background: ${theme.colors.neutral.light};
  }
`

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
`

export const OptionText = styled.p`
  margin: 0;
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weight.lg};
  color: ${theme.colors.neutral.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const SearchInput = styled.input<Pick<FieldProps, 'height'>>`
  border: 0;
  width: 100%;
  outline: none;
  min-width: 10px;
  height: ${({ height }) => `${height - 2}px`};

  font-family: ${theme.typography.family.primary};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.neutral.black};

  :disabled {
    background-color: transparent;
  }
`
